import type { Configuration, ClosedAsyncModal } from './types';
import type { InternalHookState } from './hook';

type SetShowState<T> = React.Dispatch<React.SetStateAction<InternalHookState<T> | null>>;

/**
 * Action functions for controlling the modal when it is showing. Returned by the control-actions
 * factory and composed with ControlsState to form Controls.
 */
export interface ControlsActions<T> {
  /**
   * Restart the modal, optionally with new settings. All original settings for the current session
   * are preserved. With no parameters, this will reset the `localModel` state to the
   * `initialModel`. With parameters, you can override any behavior, including providing a new
   * `initialModel`.
   *
   * If the modal is dirty, this will *not* prompt the user to confirm. The changes will be silently
   * lost. To avoid losing information, either only call `show` when `showing === false`, or add
   * your own confirmation dialog when invoking `show` and `showing === true`.
   */
  restart: ( configuration?: Partial<Configuration<T>> ) => void;
  /** Close the modal. If it's dirty, confirm first. */
  close: () => Promise<void>;
  /** Save the modal. If it rejects, saving failed. */
  save: () => Promise<void>;
  /** Update the local model (this does not save the model). */
  update: ( newValue: Partial<T> ) => void;
  /** Update a field on the local model (this does not save the model). */
  updateField: <K extends keyof Partial<T>>( field: K, newValue: Partial<T[K]> ) => void;
}

export function show<T>( setShowState: SetShowState<T> ): ClosedAsyncModal<T>['show'] {
  return ( userConfiguration ) => {
    const configuration = userConfiguration ?? {};

    setShowState( {
      configuration,
      localModel: configuration.initialModel,
      isSaving: false,
      saveSucceeded: false,
      loading: false,
      serverError: null,
    } );
  };
}

/**
 * Creates the callback that should be invoked to close the displayed modal. It may first confirm the
 * user and refuse, depending `Configuration.warnOnDirtyClose` and
 * `Configuration.isNew` flags.
 */
export function closeModal<T>(
  setShowState: SetShowState<T>,
  showState: InternalHookState<T>,
  isDirty: boolean,
): ControlsActions<T>['close'] {
  const { configuration } = showState;

  return async () => {
    if ( configuration.warnOnDirtyClose && isDirty ) {
      const prefix = configuration.isNew ? 'You have not created this item.' : 'You have unsaved changes.';
      const message = `${prefix} If you close, changes will be lost. Are you sure you want to close?`;

      if ( !confirm( message ) ) {
        return Promise.resolve();
      }
    }

    // Invoke the user's close callback.
    await configuration.closeHandler?.();
    setShowState( null );
  };
}

/**
 * Creates the callback that should be invoked to restart the displayed modal. It will reset the
 * modal to its initial state, optionally with new configuration.
 */
export function restart<T>(
  setShowState: SetShowState<T>,
  showState: InternalHookState<T>,
): ControlsActions<T>['restart'] {
  const currentConfiguration = showState.configuration;

  return ( updatedConfiguration ) =>
    setShowState( {
      ...showState,
      configuration: {
        ...currentConfiguration,
        ...updatedConfiguration,
      },
      localModel: updatedConfiguration?.initialModel ?? currentConfiguration.initialModel,
      isSaving: false,
      saveSucceeded: false,
      loading: false,
      serverError: null,
    } );
}

/**
 * Creates the callback that should be invoked to save the displayed modal. It will validate the model
 */
export function save<T>(
  setShowState: SetShowState<T>,
  showState: InternalHookState<T>,
  isModelComplete: ( model?: Partial<T> ) => model is T,
): ControlsActions<T>['save'] {
  const { localModel, configuration } = showState;

  return async () => {
    const { saveHandler, closeOnSave, closeHandler } = configuration;

    if ( !localModel ) {
      throw new Error( 'The model is not loaded. Unable to save.' );
    }

    if ( !saveHandler ) {
      throw new Error(
        'This modal is not configured for saving, but save() was invoked. Provide a `saveHandler` to make it saveable.',
      );
    }

    if ( !isModelComplete( localModel ) ) {
      throw new Error(
        'The model is not complete. Unable to save. Are you disabling save behavior when `canSave` is false?',
      );
    }

    // Apply saving state.
    setShowState( { ...showState, isSaving: true, saveSucceeded: false, loading: false, serverError: null } );

    try {
      // Invoke the user's save handler.
      await saveHandler( localModel );

      if ( closeOnSave ) {
        try {
          // Invoke the user's close handler, if provided.
          await closeHandler?.();
          // Close on save.
          setShowState( null );
        } catch {
          // Ignore errors in the close handler. That's the user's responsibility.
        }
      } else {
        // Save succeeded but we're not closing, so update state to reflect success.
        setShowState( { ...showState, isSaving: false, saveSucceeded: true, loading: false, serverError: null } );
      }
    } catch ( error: unknown ) {
      // Save failed, so update state to reflect failure.
      const serverError = ( error instanceof Error && error.message ) || 'An unknown error occurred. Unable to save.';
      setShowState( { ...showState, isSaving: false, saveSucceeded: false, serverError } );
    }
  };
}

/**
 * Creates the callback that should be invoked to update the local model entirely.
 */
export function update<T>(
  setShowState: SetShowState<T>,
  showState: InternalHookState<T>,
): ControlsActions<T>['update'] {
  return ( newModel ) => {
    setShowState( { ...showState, localModel: newModel } );
  };
}

/**
 * Creates the callback that should be invoked to update a field on the local model.
 */
export function updateField<T>(
  setShowState: SetShowState<T>,
  showState: InternalHookState<T>,
): ControlsActions<T>['updateField'] {
  const { localModel } = showState;

  return ( field, newValue ) => {
    setShowState( {
      ...showState,
      localModel: { ...localModel, [field]: newValue },
    } );
  };
}
